import React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Button, Label, TextInput, Textarea } from 'flowbite-react';
import * as wasm from 'sched';
import { v4 as uuidv4 } from 'uuid';

// Define the Zod schema for native outcome
const NativeOutcomeSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  projectId: z.string().nullable(),
});

// Define the form values type based on the schema
type NativeOutcomeFormValues = z.infer<typeof NativeOutcomeSchema>;

interface NativeOutcomeFormProps {
  onSubmit: (command: wasm.DatabaseCommand) => Promise<void>;
  onClose: () => void;
  isOpen: boolean;
  projects?: Array<{ id: string; name: string }>;
}

const NativeOutcomeForm: React.FC<NativeOutcomeFormProps> = ({ 
  onSubmit,
  onClose,
  isOpen,
  projects = []
}) => {
  
  const initialValues: NativeOutcomeFormValues = {
    title: '',
    description: '',
    projectId: null,
  };

  const handleSubmit = async (values: NativeOutcomeFormValues) => {
    // Generate a new ID for the outcome
    const outcomeId = uuidv4();
    
    // Create a NativeOutcome object
    const nativeOutcome: wasm.NativeOutcome = {
      title: values.title,
      description: values.description || null,
      project_id: values.projectId,
      deadlines: [],
      completion: {
        type: "Manual",
        complete: false,
        estimate: {
          type: "BoundedEstimate",
          min: null,
          expected: null,
          max: null
        }
      }
    };

    // Create the Outcome object (which wraps NativeOutcome)
    const outcome: wasm.Outcome = {
      type: "Native",
      ...nativeOutcome
    };

    // Create the TaskGraphNodeData object
    const taskGraphNodeData: wasm.TaskGraphNodeData = {
      type: "Outcome",
      value: outcome
    };

    // Create the command
    const command: wasm.DatabaseCommand = {
      type: "CreateItem",
      id: outcomeId,
      item: taskGraphNodeData
    };

    await onSubmit(command);
    onClose();
  };

  return (
    <div className={`${isOpen ? 'block' : 'hidden'} fixed inset-0 z-50 overflow-y-auto overflow-x-hidden flex justify-center items-center bg-cm-bg-deep/70`}>
      <div className="relative p-4 w-full max-w-4xl max-h-full">
        <div className="relative bg-cm-bg-surface rounded-lg shadow">
          {/* Header */}
          <div className="flex items-center justify-between p-4 md:p-5 border-b rounded-t">
            <h3 className="text-xl font-medium text-cm-text-primary">
              Create New Outcome
            </h3>
            <button 
              type="button" 
              className="text-cm-text-muted bg-transparent hover:bg-cm-border-subtle hover:text-cm-text-primary rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
              onClick={onClose}
            >
              <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
          </div>
          
          {/* Body */}
          <div className="p-4 md:p-5 space-y-4">
            <Formik
              initialValues={initialValues}
              validationSchema={toFormikValidationSchema(NativeOutcomeSchema)}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, errors, touched }) => (
                <Form className="space-y-4">
                  <div>
                    <Label htmlFor="title" className="mb-2 block">Title</Label>
                    <Field
                      as={TextInput}
                      id="title"
                      name="title"
                      autoFocus
                      type="text"
                      placeholder="Outcome title"
                      color={errors.title && touched.title ? 'failure' : undefined}
                    />
                    <ErrorMessage name="title" component="p" className="text-red-500 text-sm mt-1" />
                  </div>
                  
                  <div>
                    <Label htmlFor="description" className="mb-2 block">Description</Label>
                    <Field
                      as={Textarea}
                      id="description"
                      name="description"
                      placeholder="Outcome description (optional)"
                      rows={4}
                    />
                  </div>
                  
                  {projects.length > 0 && (
                    <div>
                      <Label htmlFor="projectId" className="mb-2 block">Project</Label>
                      <Field
                        as="select"
                        id="projectId"
                        name="projectId"
                        className="bg-cm-bg-elevated border border-cm-border-subtle text-cm-text-primary text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:placeholder-gray-400 dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      >
                        <option value="">None</option>
                        {projects.map(project => (
                          <option key={project.id} value={project.id}>
                            {project.name}
                          </option>
                        ))}
                      </Field>
                    </div>
                  )}
                  
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button color="light" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                      Create Outcome
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NativeOutcomeForm;
